import api from './api';

class ImagesService {
  // Subir imagen de producto
  async uploadImage(file) {
    try {
      const formData = new FormData();
      formData.append('image', file);

      console.log('📤 Subiendo imagen:', file.name);

      const response = await api.post('/images/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 30000, // 30 segundos de timeout
      });

      console.log('✅ Imagen subida exitosamente:', response.data);
      
      // Devolver la respuesta completa para compatibilidad
      return response.data;
    } catch (error) {
      console.error('❌ Error subiendo imagen:', error);
      
      if (error.response) {
        // Error del servidor
        throw new Error(error.response.data.message || 'Error del servidor al subir imagen');
      } else if (error.request) {
        // Error de conexión
        throw new Error('Error de conexión. Verifica que el servidor esté funcionando.');
      } else {
        // Error general
        throw new Error('Error procesando la imagen');
      }
    }
  }

  // Subir imagen de truck
  async uploadTruckImage(file) {
    try {
      const formData = new FormData();
      formData.append('image', file);

      console.log('📤 Subiendo imagen de truck:', file.name);

      const response = await api.post('/images/upload-truck', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 30000, // 30 segundos de timeout
      });

      console.log('✅ Imagen de truck subida exitosamente:', response.data);
      
      // Devolver la respuesta completa para compatibilidad
      return response.data;
    } catch (error) {
      console.error('❌ Error subiendo imagen de truck:', error);
      
      if (error.response) {
        // Error del servidor
        throw new Error(error.response.data.message || 'Error del servidor al subir imagen');
      } else if (error.request) {
        // Error de conexión
        throw new Error('Error de conexión. Verifica que el servidor esté funcionando.');
      } else {
        // Error general
        throw new Error('Error procesando la imagen');
      }
    }
  }
  
  // Subir imagen de requisición
  async uploadRequisitionImage(file) {
    try {
      const formData = new FormData();
      formData.append('image', file);

      console.log('📤 Subiendo imagen de requisición:', file.name);

      const response = await api.post('/images/upload-requisition', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 30000, // 30 segundos de timeout
      });

      console.log('✅ Imagen de requisición subida exitosamente:', response.data);
      
      // Devolver la respuesta completa para compatibilidad
      return response.data;
    } catch (error) {
      console.error('❌ Error subiendo imagen de requisición:', error);
      
      // Si falla con la ruta específica, intentar con la ruta genérica
      try {
        console.log('🔄 Intentando con ruta alternativa para requisición');
        return await this.uploadImage(file);
      } catch (fallbackError) {
        console.error('❌ Error en intento alternativo:', fallbackError);
        throw fallbackError;
      }
    }
  }

  // Eliminar imagen de producto
  async deleteImage(filename) {
    try {
      console.log('🗑️ Eliminando imagen:', filename);

      const response = await api.delete(`/images/delete/${filename}`);

      console.log('✅ Imagen eliminada exitosamente:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error eliminando imagen:', error);
      
      if (error.response) {
        throw new Error(error.response.data.message || 'Error del servidor al eliminar imagen');
      } else if (error.request) {
        throw new Error('Error de conexión. Verifica que el servidor esté funcionando.');
      } else {
        throw new Error('Error eliminando la imagen');
      }
    }
  }

  // Obtener URL completa de la imagen
  getImageUrl(imagePath) {
    if (!imagePath) return null;
    
    // Registro para depuración
    console.log('⚙️ Construyendo URL para imagen:', imagePath);
    
    // Si ya es una URL completa, devolverla tal como está
    if (imagePath.startsWith('http')) {
      console.log('✅ Ya es una URL completa, devolviendo tal cual');
      return imagePath;
    }
    
    // Obtener la base URL desde la instancia de api (que se configura dinámicamente)
    const apiBaseURL = api.defaults.baseURL || 'http://localhost:5000/api';
    // Remover '/api' para obtener solo la base del servidor
    const serverBaseURL = apiBaseURL.replace('/api', '');
    
    console.log('🔍 API Base URL:', apiBaseURL);
    console.log('🔍 Server Base URL:', serverBaseURL);
    
    // Si es una ruta que empieza con /images, construir URL completa
    if (imagePath.startsWith('/images')) {
      const fullUrl = `${serverBaseURL}${imagePath}`;
      console.log('✅ Construida URL con prefijo /images:', fullUrl);
      return fullUrl;
    }
    
    // Si contiene 'truck-' en el nombre, es una imagen de truck
    if (imagePath.includes('truck-')) {
      const fullUrl = `${serverBaseURL}/images/trucks/${imagePath}`;
      console.log('✅ Construida URL para imagen de truck:', fullUrl);
      return fullUrl;
    }
    
    // Si contiene 'req-' en el nombre, es una imagen de requisición
    if (imagePath.includes('req-')) {
      const fullUrl = `${serverBaseURL}/images/requisitions/${imagePath}`;
      console.log('✅ Construida URL para imagen de requisición:', fullUrl);
      return fullUrl;
    }
    
    // Por defecto, asumir que es una imagen de producto
    const fullUrl = `${serverBaseURL}/images/products/${imagePath}`;
    console.log('✅ Construida URL por defecto para producto:', fullUrl);
    return fullUrl;
  }

  // Extraer nombre de archivo de una URL
  getFilenameFromUrl(imageUrl) {
    if (!imageUrl) return null;
    
    try {
      const url = new URL(imageUrl);
      const pathSegments = url.pathname.split('/');
      return pathSegments[pathSegments.length - 1];
    } catch (error) {
      // Si no es una URL válida, asumir que es solo el nombre del archivo
      return imageUrl.split('/').pop();
    }
  }

  // Validar archivo de imagen
  validateImageFile(file) {
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!allowedTypes.includes(file.type)) {
      throw new Error('Tipo de archivo no permitido. Solo se permiten: JPG, PNG, GIF, WEBP');
    }

    if (file.size > maxSize) {
      throw new Error('El archivo es demasiado grande. Máximo 5MB permitido.');
    }

    return true;
  }
}

export const imagesService = new ImagesService();
export default imagesService;
